<template>
    <div class="orderSummary">
        <p class="orderSummary--title">{{$t('REG_SUMMARY_ORDER_SUMMARY')}}</p>
        <p v-if="hasSuccessfulCoupon" class="orderSummary--coupon-success">{{successText}}</p>
        <order-summary-label :label="summaryFlowerCopy" :hasCoupon="hasSuccessfulCoupon" :actualAmount="getPrice" :payableAmount="couponPayableAmount"/>
        <order-summary-label :label="$t('REG_SUMMARY_DELIVERY')" :hasCoupon="hasSuccessfulCoupon" :actualAmount="shippmentAmount" :payableAmount="netShippmentAmount" :alternateText="$t('REG_SUMMARY_FREE')"/>
        <order-summary-label v-if="isUSA" :label="$t('REG_SUMMARY_TAX')" :hasCoupon="hasSuccessfulCoupon" :actualAmount="taxAmount" :payableAmount="netTaxAmount" :alternateText="$t('REG_SUMMARY_TAX_TBC')"/>
        <div class="orderSummary--total">
            <p class="orderSummary--total--text">{{$t('TOTAL_INCL_VAT')}}</p>
            <p class="orderSummary--total--value">{{`${currencySymbol}${netTotalPrice}`}}</p>
        </div>
        <h4 class="orderSummary--billing">{{getBillingInfoText}}</h4>
        <p class="orderSummary--offer-code" v-if="showAddOffer" @click="openCouponField">{{$t('REG_SUMMARY_ADD_OFFER_CODE')}}</p>
        <coupon-field class="orderSummary--coupon-field" v-if="showCouponField" :appliedCoupon="couponProperties" :appliedCouponCode="displayDiscountCode" />
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
import countries from '@/resources/countries.json';
import CouponField from '@/components/forms/fields/CouponField';
import OrderSummaryLabel from '@/components/forms/fields/OrderSummaryLabel';

export default {
    name: 'OrderSummary',
    components: {
        CouponField,
        OrderSummaryLabel,
    },
    data() {
        return {
            showAddOffer: true,
        };
    },
    mounted() {
        this.showAddOffer = !this.hasSuccessfulCoupon;
    },
    computed: {
        ...mapGetters({
            couponProperties: 'register/getCouponProperties',
            taxes: 'register/getTaxes',
            deliveryAddress: 'register/getDeliveryAddress',
            selectedPlan: 'register/getSelectedPlan',
            isUSA: 'localisation/isUSA',
        }),
        currencySymbol() {
            const country = countries.filter(item => item.iso.toUpperCase() === process.env.VUE_APP_COUNTRY_ISO.toUpperCase());
            return country[0].currency.symbol;
        },
        displayDiscountCode() {
            return this.friendCode;
        },
        showCouponField() {
            return this.hasSuccessfulCoupon || !this.showAddOffer;
        },
        hasSuccessfulCoupon() {
            if (this.couponProperties) {
                return this.couponProperties.code && this.couponProperties.code.length !== 0;
            }
            return false;
        },
        successText() {
            return this.couponProperties.registration_text_2 || this.$t('REG_TEXT_2_FALLBACK');
        },
        summaryFlowerCopy() {
            const plan = this.capitalizeFirstLetter(this.selectedPlan?.plan_name);
            return this.showDiscountAmount ? this.$t('REG_SUMMARY_FIRST_WEEKLY', { plan }) : this.$t('REG_SUMMARY_FRESH_WEEKLY', { plan });
        },
        showDiscountAmount() {
            return this.couponProperties && Number(this.couponProperties.payable_amount) < Number(this.getPrice);
        },
        getPrice() {
            const boxPrice = Number(process.env.VUE_APP_BOX_PRICE);
            const shippingPrice = Number(process.env.VUE_APP_DELIVERY_PRICE || 0);
            return this.$globalQueryParams?.dci === '1' ? boxPrice : boxPrice + shippingPrice;
        },
        couponPayableAmount() {
            return (this.couponProperties && (this.couponProperties.payable_amount || 0).toFixed(2));
        },
        totalPrice() {
            return this.getPrice + this.shippmentAmount + this.taxAmount;
        },
        discountedTotalPrice() {
            return this.taxes?.discounted_total_price || 0;
        },
        netTotalPrice() {
            if (this.couponProperties) {
                let couponTotalPrice = Number(this.couponProperties?.payable_amount || 0) + Number(this.netShippmentAmount || 0) + Number(this.netTaxAmount || 0);
                this.$store.commit('register/setCheckoutPrice', couponTotalPrice.toFixed(2));
                return couponTotalPrice.toFixed(2);
            }
            const totalPrice = this.totalPrice - this.discountedTotalPrice;
            this.$store.commit('register/setCheckoutPrice', totalPrice.toFixed(2));
            return totalPrice;
        },
        shippmentAmount() {
            return this.$globalQueryParams?.dci === '1' ? Number(process.env.VUE_APP_DELIVERY_PRICE || 0) : 0;
        },
        discountShippmentAmount() {
            return this.taxes?.discount_shipping_amount || 0;
        },
        netShippmentAmount() {
            if (this.couponProperties && this.couponProperties?.payable_amount === 0) return 0;
            let balance = this.shippmentAmount - this.discountShippmentAmount;
            return balance <= 0 ? 0 : balance;
        },
        taxAmount() {
            return this.taxes?.tax || 0;
        },
        discountedTaxAmount() {
            return this.taxes?.discounted_tax || 0;
        },
        netTaxAmount() {
            let netTaxAmt = this.taxAmount - this.discountedTaxAmount;
            return netTaxAmt.toFixed(2);
        },
        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },
        deliveryDay() {
            return this.$store.getters['register/getFirstDeliveryDay'];
        },
        getBillingInfoText() {
            if (this.deliveryDay) {
                return this.$t('REG_SUMMARY_BILLING_INFO_DAY', { day: this.deliveryDay });
            }
            return this.$t('REG_SUMMARY_BILLING_INFO');
        },
    },
    methods: {
        openCouponField() {
            this.showAddOffer = false;
        },
        capitalizeFirstLetter(value) {
            return value && value.charAt(0).toLowerCase() + value.slice(1);
        },
    },
};
</script>

<style lang="scss" scoped>
.orderSummary{
    max-width: 400px;
    margin: 2rem auto;
    padding: 0 1rem;
    &--title {
        font-family: 'gillsans';
        text-align: center;
        font-size: 21px;
        color: theme('colors.shop.black');
        font-weight: 600;
    }
    &--delivery {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 auto;
        p {
            font-size: 20px;
        }
    }
    &--total {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 auto;
        margin-top: 1rem;
        margin-bottom: 1rem;
        &--text, &--value {
            margin: 0;
            font-size: 20px;
            text-align: left;
            font-weight: 400;
            color: theme('colors.shop.black');
        }
        &--value {
            text-align: right;
        }
    }
    &--billing {
        font-family: 'gillsans';
        text-align: center;
        font-size: 17px;
        line-height: 22px;
        font-weight: 400;
        color: theme('colors.shop.black');
    }
    &--offer-code {
        text-align: center;
        font-size: 18px;
        font-weight: lighter;
        margin: 1rem auto;
        text-decoration: underline;
        cursor: pointer;
    }
    &--coupon-field {
        margin: 1rem auto;
        padding: 0;
    }
    &--coupon-success {
        text-align: center;
        margin: 1rem auto;
        margin-bottom: 1.5rem;
        font-size: 20px;
        line-height: 25px;
    }
}

.taxes-style {
    text-decoration: line-through;
}
</style>
